import { backendPublicPath } from "@/api/servicePaths";

import FETCHRAW from "../fetchRaw";

export const createSandboxTenant = async (body: {
  tenant_name: string;
  sql_dump_file: string;
  current_user_password: string;
  is_oauth_user?: boolean;
}): Promise<any> => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/tenants/load_sample_data/"),
    method: "POST",
    body: JSON.stringify(body)
  });
};

export const getTenantsForTable = async ({
  params,
  serverSideReq
}: {
  params: {
    [x: string]: string;
  };
  serverSideReq?: any;
}) => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/tenants/sandbox_list/"),
    method: "GET",
    params: params,
    serverSideReq
  });
};

export const deleteTenant = async (body: { tenant_guid: string }): Promise<any> => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/tenants/sandbox_list/"),
    method: "DELETE",
    body: JSON.stringify(body)
  });
};

export const createTenantFromExisting = async (body: {
  tenant_name: string;
  current_user_password: string;
  is_oauth_user?: boolean;
}): Promise<any> => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/tenants/load_existing_data/"),
    method: "POST",
    body: JSON.stringify(body)
  });
};

export const setSandboxTenantPassword = async (body: { tenant_name: string; new_password: string }): Promise<any> => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/tenants/set_sandbox_password/"),
    method: "POST",
    body: JSON.stringify(body)
  });
};

/**
 * Add sample/demo data to a tenant.
 * @param body - Object containing tenant_guid and sample data parameters, including num_campaigns.
 * @returns API response JSON.
 */
export const addSampleDataToTenant = async (body: {
  tenant_guid: string;
  use_case_template_id?: string;
  num_accounts?: number;
  num_customers_per_account?: number;
  num_products?: number;
  num_users?: number;
  num_campaigns?: number;
  num_opportunities?: number;
  fiscal_year?: number;
  fiscal_quarter?: number;
  org_structure?: object;
  quota_config?: object;
  team_config?: object;
  products?: any;
  accounts?: any;
  customers?: any;
  campaigns?: any;
  opportunity_hints?: any;
  activity_hints?: any;
  opportunity_contact_roles?: any;
  // Optional: total activity counts for previous and current quarters.
  // These are used by the backend to distribute activities across dates.
  prev_quarter_activity_count?: number;
  curr_quarter_activity_count?: number;
  num_signals?: number;
  user_hints?: any;
  num_teams?: number;
  include_learning_proposals?: boolean;
  include_change_review_samples?: boolean;
}): Promise<any> => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/tenants/add_sample_data_to_current_tenant/"),
    method: "POST",
    body: JSON.stringify(body)
  });
};

/**
 * Download a tenant snapshot (JSON or ZIP) as a Blob.
 * @param tenantGuid - The GUID of the tenant to export.
 * @param dumpFormat - The export format ("json" or "csv" or "zip"). Defaults to "json".
 * @returns Blob of the exported data.
 */
export const downloadTenantSnapshot = async (tenantGuid: string, dumpFormat: string = "json"): Promise<Blob> => {
  // Always request as JSON, since backend may return either JSON or file
  const resp = await FETCHRAW({
    type: "apiurl",
    path: backendPublicPath(`/tenants/dump_tenant_data/?tenant_guid=${tenantGuid}&dump_format=${dumpFormat}`),
    method: "GET",
    serverSideReq: true,
    responseType: "json"
  });

  if (resp && resp.content) {
    // If backend returns a presigned S3 URL, fetch the file as Blob
    if (resp.content.download_url) {
      const s3Resp = await fetch(resp.content.download_url);
      if (!s3Resp.ok) {
        throw new Error("Failed to fetch S3 export file: " + s3Resp.statusText);
      }
      const s3Blob = await s3Resp.blob();
      return s3Blob;
    }
    // If backend returns a Blob (legacy/local file), handle as before
    if (resp.content instanceof Blob) {
      return resp.content as Blob;
    }
    // Error handling
    if (resp.content.error) {
      throw new Error("Backend error: " + resp.content.error);
    }
    if (resp.content.message) {
      throw new Error("Backend message: " + resp.content.message);
    }
  }
  throw new Error("Failed to download tenant snapshot: resp.content is not a Blob or valid JSON with download_url");
};

/**
 * Create and import a tenant using FormData.
 * @param formData - FormData containing tenant creation/import fields.
 * @returns API response JSON.
 */
export const createAndImportTenant = async (formData: FormData): Promise<any> => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/tenants/create_and_import_tenant/"),
    method: "POST",
    body: formData,
    serverSideReq: true
  });
};

/**
 * Fetch management info for a tenant.
 * @param tenantGuid - The GUID of the tenant.
 * @returns Tenant info object.
 */
export const getManagementTenantInfo = async (tenantGuid: string): Promise<any> => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath(`/tenants_profile/tenants/${tenantGuid}/`),
    method: "GET"
  });
};

export const mutateTenantRuntimeLlmKey = async (tenantGuid: string, action: "provision" | "rotate"): Promise<any> => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath(`/tenants_profile/tenants/${tenantGuid}/llm-runtime-key/`),
    method: "POST",
    body: JSON.stringify({ action })
  });
};

export const testTenantRuntimeLlmKey = async (tenantGuid: string): Promise<any> => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath(`/tenants_profile/tenants/${tenantGuid}/llm-runtime/test/`),
    method: "POST",
    body: JSON.stringify({})
  });
};

/**
 * Update tenant info.
 * @param tenantGuid - The GUID of the tenant.
 * @param body - The updated tenant data.
 * @returns API response JSON.
 */
export const updateTenant = async (tenantGuid: string, body: any): Promise<any> => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath(`/tenants_profile/tenants/${tenantGuid}/`),
    method: "PUT",
    body: JSON.stringify(body)
  });
};

/**
 * Partially update tenant info (PATCH).
 * @param tenantGuid - The GUID of the tenant.
 * @param body - Partial tenant payload.
 */
export const patchTenant = async (tenantGuid: string, body: any): Promise<any> => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath(`/tenants_profile/tenants/${tenantGuid}/`),
    method: "PATCH",
    body: JSON.stringify(body)
  });
};

/**
 * Fetch status logs for a tenant.
 * @param tenantGuid - The GUID of the tenant.
 * @returns Array of status log entries.
 */
export const getTenantStatusLogs = async (tenantGuid: string, params?: Record<string, any>): Promise<any> => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath(`/tenants/${tenantGuid}/status-logs/`),
    method: "GET",
    params: params
  });
};

/**
 * Cleanup generated sample data for a tenant.
 */
export const cleanupSampleDataForTenant = async (body: { tenant_guid: string }): Promise<any> => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/tenants/cleanup_sample_data_for_tenant/"),
    method: "POST",
    body: JSON.stringify(body)
  });
};

/**
 * Update a tenant LLM key by id (PUT).
 * @param id - LLM key id (uuid or int)
 * @param body - { llmkey, config }
 * @returns API response JSON.
 */
export const updateTenantLlmKey = async (
  id: string | number,
  body: {
    model_type?: string;
    display_name?: string;
    description?: string;
    llmkey?: string;
    config: Record<string, string>;
  }
): Promise<any> => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath(`/tenants_profile/llm-key/${id}/`),
    method: "PUT",
    body: JSON.stringify(body)
  });
};

/**
 * Create a new tenant LLM key (POST).
 * @param model_type - Provider type (gpt, claude, gemini)
 * @param llmkey - API key string
 * @param config - config object
 * @returns API response JSON.
 */
export const createTenantLlmKey = async (
  model_type: string,
  display_name: string,
  description: string,
  llmkey: string,
  config: Record<string, string>
): Promise<any> => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath(`/tenants_profile/llm-key/`),
    method: "POST",
    body: JSON.stringify({ model_type, display_name, description, llmkey, config })
  });
};

/**
 * Delete a tenant LLM key by id (DELETE).
 * @param id - LLM key id (uuid or int)
 * @returns API response JSON.
 */
export const deleteTenantLlmKey = async (id: string | number): Promise<any> => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath(`/tenants_profile/llm-key/${id}/`),
    method: "DELETE"
  });
};

export const validateTenantLlmKey = async (tenantGuid: string, modelType: string): Promise<any> => {
  return FETCHRAW({
    type: "apiurl",
    path: backendPublicPath(`/tenants_profile/tenants/${tenantGuid}/llm-keys/${modelType}/validate/`),
    method: "POST",
    body: JSON.stringify({})
  });
};

export interface HelpdeskAdminSessionSummary {
  session_id: string;
  request_id: string;
  mutation_id: string;
  action_id: string;
  state: string;
  user_ref: string;
  device_ref: string;
  policy_hash: string;
  failure_reason: string;
  problem_statement: string;
  updated_at: string | null;
  opened_at: string | null;
}

export interface HelpdeskAdminSessionListResponse {
  code: number;
  count: number;
  offset: number;
  limit: number;
  sessions: HelpdeskAdminSessionSummary[];
}

export interface HelpdeskAdminSessionDetailResponse {
  code: number;
  session: Record<string, any>;
  recommendations: Record<string, any>[];
  proposals: Record<string, any>[];
  feedback: Record<string, any>[];
  evidence: Record<string, any>[];
  mutations: Record<string, any>[];
  timeline: Record<string, any>[];
  latest_mutation_id: string;
}

export interface HelpdeskAdminSupportBundleResponse {
  code: number;
  bundle: Record<string, any>;
}

export const getHelpdeskAdminSessions = async (
  tenantGuid: string,
  params?: Record<string, string | number | undefined>
): Promise<HelpdeskAdminSessionListResponse> => {
  const response = await FETCHRAW({
    type: "apiurl",
    path: backendPublicPath(`/edge-agent/helpdesk/admin/sessions/`),
    method: "GET",
    params: {
      ...params,
      tenant_guid: tenantGuid
    }
  });

  return response.content as HelpdeskAdminSessionListResponse;
};

export const getHelpdeskAdminSessionDetail = async (
  tenantGuid: string,
  sessionId: string
): Promise<HelpdeskAdminSessionDetailResponse> => {
  const response = await FETCHRAW({
    type: "apiurl",
    path: backendPublicPath(`/edge-agent/helpdesk/admin/sessions/${sessionId}/`),
    method: "GET",
    params: {
      tenant_guid: tenantGuid
    }
  });

  return response.content as HelpdeskAdminSessionDetailResponse;
};

export const getHelpdeskAdminSupportBundle = async (
  tenantGuid: string,
  sessionId: string
): Promise<HelpdeskAdminSupportBundleResponse> => {
  const response = await FETCHRAW({
    type: "apiurl",
    path: backendPublicPath(`/edge-agent/helpdesk/admin/sessions/${sessionId}/support-bundle/`),
    method: "GET",
    params: {
      tenant_guid: tenantGuid
    }
  });

  return response.content as HelpdeskAdminSupportBundleResponse;
};
